import { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Home from "./pages/Home";
import Status from "./pages/Status";
import Spoofer from "./pages/Spoofer";
import Faq from "./pages/Faq";
import Dma from "./pages/Dma";
import Termsofuse from "./pages/Termsofuse";
import Privacy from "./pages/Privacy";
import Delivery from "./pages/Delivery";
import Blog from "./pages/Blog";
import Manual from "./pages/Manual";
import AdminStatusPanel from "./pages/AdminStatusPanel";
import ProductRoute from "./pages/games/ProductRoute";
import { ArcadeBackground, LiveChatWidget, SiteNavbar } from "./shared/site";
import { apiUrl } from "./shared/api";

const terminalBootLines = [
  "mounting arcade interface",
  "validating store identity",
  "syncing product matrix",
  "opening secure channel",
];

const terminalBootDurationMs = 6400;
const terminalBootStorageKey = "hs_terminal_boot_seen_v2";

function TerminalBootIntro() {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);
  const bootStartedAtRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion || location.pathname !== "/") return undefined;
    if (!bootStartedAtRef.current && localStorage.getItem(terminalBootStorageKey) === "true") return undefined;

    if (!bootStartedAtRef.current) {
      bootStartedAtRef.current = Date.now();
      setIsVisible(true);
      localStorage.setItem(terminalBootStorageKey, "true");
    }

    const elapsedMs = Date.now() - bootStartedAtRef.current;
    const timeoutId = setTimeout(() => setIsVisible(false), Math.max(0, terminalBootDurationMs - elapsedMs));
    return () => clearTimeout(timeoutId);
  }, [location.pathname, prefersReducedMotion]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="terminal-boot-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          aria-hidden="true"
        >
          <span className="terminal-boot-backdrop" />
          <div className="terminal-boot-stage">
            <span className="terminal-boot-particle terminal-boot-particle-one" />
            <span className="terminal-boot-particle terminal-boot-particle-two" />
            <span className="terminal-boot-particle terminal-boot-particle-three" />
            <span className="terminal-boot-particle terminal-boot-particle-four" />

            <div className="terminal-boot-portal">
              <span className="terminal-boot-ring terminal-boot-ring-one" />
              <span className="terminal-boot-ring terminal-boot-ring-two" />
              <span className="terminal-boot-core">HS</span>
              <span className="terminal-boot-beam" />
            </div>

            <div className="terminal-boot-grid" />
          <div className="terminal-boot-window">
            <div className="terminal-boot-header">
              <div className="terminal-boot-lights">
                <span />
                <span />
                <span />
              </div>
              <p>HACKSELLER TERMINAL</p>
              <strong>BOOT</strong>
            </div>
            <div className="terminal-boot-body">
              <div className="terminal-boot-command-panel">
                <p className="terminal-boot-prompt">
                  <span className="terminal-boot-user">root@hackseller</span>
                  <span className="terminal-boot-path">:/store$</span>
                  <span className="terminal-boot-command">launch HACKSELLER.STORE</span>
                  <span className="terminal-boot-cursor" />
                  <span className="terminal-boot-enter">ENTER</span>
                </p>
                <div className="terminal-boot-code">
                  {terminalBootLines.map((line, index) => (
                    <p key={line} style={{ "--line-index": index }}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>

              <div className="terminal-boot-route-panel">
                <div className="terminal-boot-route-map">
                  <span className="terminal-boot-route-node terminal-boot-route-node-client">YOU</span>
                  <span className="terminal-boot-route-line" />
                  <span className="terminal-boot-route-packet" />
                  <span className="terminal-boot-route-node terminal-boot-route-node-store">HS</span>
                </div>
                <div className="terminal-boot-meta">
                  <span>ARCADE</span>
                  <span>SSL OK</span>
                  <span>HOME READY</span>
                </div>
                <div className="terminal-boot-progress">
                  <span />
                </div>
              </div>
            </div>
          </div>
          </div>
          <motion.div
            className="terminal-boot-flash"
            initial={{ opacity: 0, scaleY: 0.04 }}
            animate={{ opacity: [0, 0, 0.75, 0], scaleY: [0.04, 0.04, 0.9, 1.1] }}
            transition={{ duration: 6.35, times: [0, 0.86, 0.94, 1], ease: [0.22, 1, 0.36, 1] }}
            onAnimationComplete={() => setIsVisible(false)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  const isProductRoute = location.pathname.startsWith("/games/");
  const routeTransition = getRouteTransition(location.pathname);
  const useStaticRouteShell = prefersReducedMotion;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={`${location.pathname}${location.search}`}
        className={`page-route-shell page-route-${routeTransition.type}${isProductRoute ? " page-route-shell-product" : ""}`}
        initial={useStaticRouteShell ? false : routeTransition.initial}
        animate={routeTransition.animate}
        exit={useStaticRouteShell ? { opacity: 1 } : routeTransition.exit}
        transition={routeTransition.transition}
      >
        {!useStaticRouteShell && (
          <motion.span
            className="page-route-progress"
            initial={{ scaleX: 0, opacity: 0.9 }}
            animate={{ scaleX: 1, opacity: 0 }}
            transition={{ duration: 0.52, ease: [0.22, 1, 0.36, 1] }}
            aria-hidden="true"
          />
        )}
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/Spoofer" element={<Spoofer />} />
          <Route path="/Status" element={<Status />} />
          <Route path="/Dma" element={<Dma />} />
          <Route path="/Termsofuse" element={<Termsofuse />} />
          <Route path="/Privacy" element={<Privacy />} />
          <Route path="/Delivery" element={<Delivery />} />
          <Route path="/Blog" element={<Blog />} />
          <Route path="/Manual" element={<Manual />} />
          <Route path="/ops-vault-a91f6d" element={<AdminStatusPanel />} />
          <Route path="/games/:gameId" element={<ProductRoute />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function getRouteTransition(pathname) {
  const base = {
    animate: { opacity: 1, x: 0, y: 0, scale: 1, filter: "blur(0px)" },
    transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
  };

  if (pathname === "/") {
    return {
      ...base,
      type: "home",
      initial: { opacity: 0, y: 18, scale: 0.992, filter: "blur(10px)" },
      exit: { opacity: 0, y: -12, scale: 1.006, filter: "blur(8px)" },
      transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
    };
  }

  if (pathname.startsWith("/games/")) {
    return {
      ...base,
      type: "product",
      initial: { opacity: 0, x: 28, filter: "blur(8px)" },
      exit: { opacity: 0, x: -20, filter: "blur(7px)" },
      transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] },
    };
  }

  if (["/Status", "/Spoofer", "/Dma"].includes(pathname)) {
    return {
      ...base,
      type: "catalog",
      initial: { opacity: 0, y: 22, filter: "blur(7px)" },
      exit: { opacity: 0, y: -16, filter: "blur(6px)" },
    };
  }

  if (pathname === "/ops-vault-a91f6d") {
    return {
      ...base,
      type: "admin",
      initial: { opacity: 0, scale: 0.985, filter: "blur(6px)" },
      exit: { opacity: 0, scale: 0.99, filter: "blur(6px)" },
      transition: { duration: 0.2, ease: "easeOut" },
    };
  }

  if (["/Termsofuse", "/Privacy", "/Delivery", "/Blog", "/Manual", "/faq"].includes(pathname)) {
    return {
      ...base,
      type: "info",
      initial: { opacity: 0, y: 14 },
      exit: { opacity: 0, y: -10 },
      transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
    };
  }

  return {
    ...base,
    type: "default",
    initial: { opacity: 0 },
    exit: { opacity: 0 },
  };
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    document.body.dataset.siteSurface = pathname === "/ops-vault-a91f6d" ? "admin" : "public";

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);

  return null;
}

const visitorNoticeStorageKey = "hs_visitor_notice_seen";
const visitorIdStorageKey = "hs_visitor_id";

function VisitorNoticeBanner({ isHidden, onUnderstand }) {
  const location = useLocation();
  const shouldDelayHomeIntroRef = useRef(location.pathname === "/" && localStorage.getItem(terminalBootStorageKey) !== "true");
  const [introDelayDone, setIntroDelayDone] = useState(() => {
    return !shouldDelayHomeIntroRef.current;
  });

  useEffect(() => {
    if (location.pathname !== "/" || !shouldDelayHomeIntroRef.current) {
      setIntroDelayDone(true);
      return undefined;
    }

    setIntroDelayDone(false);
    const timeoutId = setTimeout(() => setIntroDelayDone(true), terminalBootDurationMs + 450);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  if (!introDelayDone || isHidden || location.pathname === "/ops-vault-a91f6d" || window.self !== window.top) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 text-white font-jersey">
      <div className="mx-auto overflow-hidden rounded-lg border border-purple-300/30 bg-gray-950/90 shadow-[0_18px_80px_rgba(0,0,0,0.72),0_0_36px_rgba(124,58,237,0.18)] backdrop-blur-xl md:max-w-5xl">
        <div className="h-px bg-gradient-to-r from-transparent via-purple-300/70 to-transparent" />
        <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 gap-3">
            <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-purple-300/30 bg-purple-500/15 text-xl font-extrabold text-purple-100 shadow-[0_0_24px_rgba(168,85,247,0.22)] sm:flex">
              HS
            </div>
            <div className="min-w-0">
              <p className="text-sm font-extrabold uppercase tracking-normal text-purple-300">Site notice</p>
              <p className="mt-0.5 text-lg font-bold leading-snug text-gray-200">
            We use limited site activity data to monitor active visits, improve support, and keep the service secure.
              </p>
            </div>
          </div>
          <div className="flex shrink-0">
            <button
              type="button"
              onClick={onUnderstand}
              className="rounded-lg border border-purple-200/50 bg-purple-600 px-5 py-2.5 text-lg font-extrabold text-white shadow-[0_0_22px_rgba(124,58,237,0.26)] transition hover:border-purple-100 hover:bg-purple-500"
            >
              I Understand
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VisitorHeartbeat() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/ops-vault-a91f6d" || window.self !== window.top) return undefined;

    let visitorId = localStorage.getItem(visitorIdStorageKey);
    if (!visitorId) {
      visitorId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(visitorIdStorageKey, visitorId);
    }

    const sendHeartbeat = () => {
      const dataEntries = typeof performance !== "undefined"
        ? performance.getEntriesByType("resource").filter((entry) => {
            try {
              return new URL(entry.name).pathname.match(/^\/data\/[^/]+\.json$/i);
            } catch {
              return false;
            }
          })
        : [];

      fetch(apiUrl("/api/visitor/heartbeat"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visitorId,
          page: `${location.pathname}${location.search}`,
          referrer: document.referrer,
          title: document.title,
          screen: window.screen ? `${window.screen.width}x${window.screen.height}` : "",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          platform: navigator.platform || "",
          dataRequestCount: dataEntries.length,
          dataFiles: [...new Set(dataEntries.map((entry) => {
            try {
              return new URL(entry.name).pathname;
            } catch {
              return "";
            }
          }).filter(Boolean))].slice(0, 30),
        }),
      }).catch(() => null);
    };

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 25000);
    return () => clearInterval(intervalId);
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const [visitorNoticeSeen, setVisitorNoticeSeen] = useState(() => localStorage.getItem(visitorNoticeStorageKey) === "true");

  const hideVisitorNotice = () => {
    localStorage.setItem(visitorNoticeStorageKey, "true");
    setVisitorNoticeSeen(true);
  };

  return (
    <Router>
      <ScrollToTop />
      <VisitorHeartbeat />
      <ArcadeBackground />
      <div className="relative z-10">
        <SiteNavbar global />
        <AnimatedRoutes />
        <TerminalBootIntro />
        <LiveChatWidget />
      </div>
      <VisitorNoticeBanner isHidden={visitorNoticeSeen} onUnderstand={hideVisitorNotice} />
    </Router>
  );
}

export default App;
